// allowedClaims convention: Root is NOT listed alongside other claims — it is
// granted by the bypass in common/Auth/index.js (isRootOrOwner) that router.ts
// runs before evaluating the list. List SystemClaim.Root only when Root is the
// sole audience.
import { isDev } from '@/config';

const MainLayout = () =>
	import('src/components/Dashboard/Layout/MainLayout.vue');
const NotFound = () => import('src/components/GeneralViews/NotFoundPage.vue');
const NoAccess = () => import('src/components/GeneralViews/NoAccessPage.vue');
const Unblock = () => import('../components/GeneralViews/Ublock.vue');
const Login = () => import('src/components/Dashboard/Views/Pages/Login.vue');
const ForgotPass = () =>
	import('src/components/Dashboard/Views/Pages/ForgotPass.vue');
const ConfirmEmailChange = () =>
	import('../components/Dashboard/Views/Pages/confirm_email.vue');
const ResetPass = () =>
	import('src/components/Dashboard/Views/Pages/reset_password.vue');
const TwoFactor = () =>
	import('src/components/Dashboard/Views/Pages/TwoFALogin.vue');
const TwoFASetupRequired = () =>
	import('src/components/Dashboard/Views/Pages/TwoFASetupRequired.vue');
const PublicPageLayout = () =>
	import('@/components/Marketing/Layout/PublicPageLayout/PublicPageLayout.vue');
const HomePage = () =>
	import('@/components/Marketing/Pages/HomePage/HomePage.vue');
const MainDashboard = () =>
	import('@/components/Dashboard/Views/Dashboard/Main.vue');
const BillingOverview = () =>
	import('@/components/Dashboard/Views/Billing/BillingOverview.vue');
const BillingManage = () =>
	import('@/components/Dashboard/Views/Billing/BillingManage.vue');
const BillingSubscribe = () =>
	import('@/components/Dashboard/Views/Billing/BillingSubscribe.vue');
const ReleaseNotes = () =>
	import('@/components/Marketing/Pages/ReleaseNotesPage/ReleaseNotesPage.vue');
const Docs = () => import('@/components/Marketing/Pages/DocsPage/DocsPage.vue');
const KitchenSinkOverview = () =>
	import('@/components/Dashboard/Views/KitchenSink/Home/Overview.vue');
const KitchenSinkIcons = () =>
	import('@/components/Dashboard/Views/KitchenSink/Icons/Icons.vue');
const KitchenSinkButtons = () =>
	import('@/components/Dashboard/Views/KitchenSink/Buttons/Buttons.vue');
const KitchenSinkColors = () =>
	import('@/components/Dashboard/Views/KitchenSink/Colors/Colors.vue');
const KitchenSinkCard = () =>
	import('@/components/Dashboard/Views/KitchenSink/Card/Card.vue');
const KitchenSinkForm = () =>
	import('@/components/Dashboard/Views/KitchenSink/Form/Form.vue');
const KitchenSinkLayout = () =>
	import('@/components/Dashboard/Views/KitchenSink/Layout/Layout.vue');

import { ModuleRole, SystemClaim } from '@/common/rbac';
import { BILLING_TYPE } from '@/constants/billing/billingType';
import { PAYMENT_METHOD } from '@/constants/billing/paymentMethod';
const AMLSearch = () => import('src/components/Dashboard/Views/AML/Search.vue');
const AMLHistory = () =>
	import('src/components/Dashboard/Views/AML/History.vue');
const Billing = () =>
	import('../components/Dashboard/Views/Reports/Billing.vue');
const BillingDetail = () =>
	import('../components/Dashboard/Views/Reports/BillingDetail');
const AMLSearchResults = () =>
	import('src/components/Dashboard/Views/AML/SearchResults.vue');
const AMLSearchDetails = () =>
	import('src/components/Dashboard/Views/AML/SearchDetails.vue');
const CreateForm = () =>
	import('src/components/Dashboard/Views/Forms/Create.vue');
const FormsList = () =>
	import('src/components/Dashboard/Views/Forms/FormsList.vue');
const EditForm = () => import('src/components/Dashboard/Views/Forms/Edit.vue');
const FormHistory = () =>
	import('src/components/Dashboard/Views/Forms/FormHistory.vue');
const FormSubmissionDetails = () =>
	import('src/components/Dashboard/Views/Forms/FormSubmissionDetails.vue');
const FormCompletion = () =>
	import('src/components/Dashboard/Views/Forms/FormCompletion.vue');

const AMLBatchSearch = () =>
	import('src/components/Dashboard/Views/AML/Batch/Search.vue');
const AMLBatchHistory = () =>
	import('src/components/Dashboard/Views/AML/Batch/History.vue');
const AMLBatchDetails = () =>
	import('src/components/Dashboard/Views/AML/Batch/Details.vue');
const AMLBatchDownloadDetails = () =>
	import('src/components/Dashboard/Views/AML/Batch/DownloadDetails.vue');
const CaseCreate = () =>
	import('src/components/Dashboard/Views/Case/Create.vue');
const CaseHistory = () =>
	import('src/components/Dashboard/Views/Case/History.vue');
const CaseExports = () =>
	import('src/components/Dashboard/Views/Case/Exports.vue');
const CaseExportDetails = () =>
	import('src/components/Dashboard/Views/Case/ExportDetails.vue');

const CaseDetailsWrapper = () =>
	import('src/components/Dashboard/Views/Case/CaseDetails/CaseDetailsWrapper/CaseDetailsWrapper.vue');

const CaseTemplates = () =>
	import('src/components/Dashboard/Views/Settings/CaseTemplates.vue');
const MerchantAgents = () =>
	import('src/components/Dashboard/Views/Merchants/Agents.vue');
const UserManagementRoles = () =>
	import('src/components/Dashboard/Views/UserManagement/Roles.vue');

const UserManagementNewRole = () =>
	import('src/components/Dashboard/Views/UserManagement/NewRole.vue');
const UserManagementEditRole = () =>
	import('src/components/Dashboard/Views/UserManagement/EditRole.vue');
const UserManagementUsers = () =>
	import('src/components/Dashboard/Views/UserManagement/Users.vue');
const UserManagementUserProfile = () =>
	import('src/components/Dashboard/Views/UserManagement/UserProfile.vue');
const UserDetailsCardNew = () =>
	import('src/components/Dashboard/Views/UserManagement/Cards/UserDetailsCardNew/UserDetailsCardNew.vue');
const UserManagementNewUser = () =>
	import('src/components/Dashboard/Views/UserManagement/NewUser.vue');
const MerchantList = () =>
	import('src/components/Dashboard/Views/Merchants/List.vue');
const MerchantNew = () =>
	import('src/components/Dashboard/Views/Merchants/New.vue');
const MerchantProfile = () =>
	import('src/components/Dashboard/Views/Merchants/Profile.vue');
const MerchantReport = () =>
	import('src/components/Dashboard/Views/Merchants/Report.vue');

const CreateAPIKey = () =>
	import('src/components/Dashboard/Views/API/CreateAPIKey.vue');
const APIKeyList = () => import('src/components/Dashboard/Views/API/List.vue');
const APIKeyDetails = () =>
	import('src/components/Dashboard/Views/API/Details.vue');
const Logs = () => import('src/components/Dashboard/Views/API/Analyser.vue');
const LogDetails = () =>
	import('src/components/Dashboard/Views/API/LogDetails.vue');
const CreateWebhook = () =>
	import('src/components/Dashboard/Views/API/CreateWebhook.vue');
const Webhooks = () =>
	import('src/components/Dashboard/Views/API/Webhooks.vue');
const EventLogs = () =>
	import('src/components/Dashboard/Views/API/EventLogs.vue');
const EventLogDetails = () =>
	import('src/components/Dashboard/Views/API/EventLogDetails.vue');

const Archive = () =>
	import('src/components/Dashboard/Views/Settings/Archive/Archive.vue');
const ProofOfAddressNew = () =>
	import('src/components/Dashboard/Views/Settings/ProofOfAddressNew.vue');
const ProofOfAddressDetail = () =>
	import('src/components/Dashboard/Views/Settings/ProofOfAddressDetail.vue');
const ProofOfAddressList = () =>
	import('src/components/Dashboard/Views/Settings/ProofOfAddressList.vue');
const CfmThemeCustomizer = () =>
	import('src/components/Dashboard/Views/Settings/CfmSettings/CfmThemeCustomizer/CfmThemeCustomizer.vue');
const FlowsGeneralSettings = () =>
	import('src/components/Dashboard/Views/Settings/CfmSettings/General/FlowsGeneralSettings.vue');
const FlowsOverview = () =>
	import('src/components/Dashboard/Views/Settings/CfmSettings/FlowsOverview.vue');
const FlowsSettings = () =>
	import('src/components/Dashboard/Views/Settings/CfmSettings/FlowsSettings.vue');
const RiskLevels = () =>
	import('src/components/Dashboard/Views/Settings/CfmSettings/RiskLevels/RiskLevels.vue');
const RiskLevel = () =>
	import('src/components/Dashboard/Views/Settings/CfmSettings/RiskLevels/RiskLevel.vue');

const SupportedCheck = () =>
	import('src/components/Dashboard/Views/Settings/CfmSettings/SupportedChecks/SupportedCheck.vue');
const SupportedChecks = () =>
	import('src/components/Dashboard/Views/Settings/CfmSettings/SupportedChecks/SupportedChecks.vue');

const Trigger = () =>
	import('src/components/Dashboard/Views/Settings/TriggerSettings/Triggers.vue');
const CreateTrigger = () =>
	import('src/components/Dashboard/Views/Settings/TriggerSettings/CreateTrigger.vue');
const TriggerDetails = () =>
	import('src/components/Dashboard/Views/Settings/TriggerSettings/TriggerDetails.vue');

const NotificationsConfiguration = () =>
	import('src/components/Dashboard/Views/Notifications/Configuration.vue');
const NotificationsCustomization = () =>
	import('src/components/Dashboard/Views/Notifications/Customization.vue');
const NotificationsTemplateDetails = () =>
	import('src/components/Dashboard/Views/Notifications/TemplateDetails.vue');
const NotificationsList = () =>
	import('src/components/Dashboard/Views/Notifications/History.vue');

const TemplateList = () =>
	import('src/components/Dashboard/Views/Settings/TemplateSettings/Templates.vue');
const TemplateDetails = () =>
	import('src/components/Dashboard/Views/Settings/TemplateSettings/TemplateDetails.vue');

interface RoutePath {
	path: string;
	name?: string;
	component?: any;
	components?: any;
	// A function lets a parent land each role on a child it is allowed to see,
	// instead of redirecting everyone to one child and letting the router guard
	// bounce those who cannot open it.
	redirect?: string | (() => string);
	children?: RoutePath[];
	props?: any;
	meta?: {
		title?: string;
		allowedClaims?: string[];
		hideForBillingType?: string[];
		hideForPaymentMethod?: string[];
		requiresNoAuth?: boolean;
		allowAuthenticated?: boolean;
		hideMarketingNavigation?: boolean;
		// Mounts a component that also serves an editable route in read-only mode,
		// e.g. 'View Form' reusing the template builder.
		readOnly?: boolean;
		reuse?: boolean; // if true, the component will not be re-rendered when navigating to the same route but the query params change. This is useful for list pages where the query params change but the component itself does not need to be re-rendered.
		reuseKey?: string;
		splitFallbackRouteName?: string;
		splitMainRouteName?: string;
	};
}

// Where the guard sends a signed-in person whose role (or account setup) does not
// cover the page they asked for. Inside the main layout, so the sidebar stays and
// the page reads as part of the app rather than an error screen; open to everyone,
// or the guard would send them here and bounce them again.
const noAccessPage: RoutePath = {
	path: '/no-access',
	component: MainLayout,
	children: [
		{
			path: '',
			name: 'No Access',
			component: NoAccess,
			meta: {
				allowedClaims: ['All', SystemClaim.Agent],
			},
		},
	],
};

const dashboardPages: RoutePath = {
	path: '/dashboard',
	component: MainLayout,
	redirect: '/dashboard/main',
	children: [
		{
			path: 'main',
			name: 'Dashboard Main',
			component: MainDashboard,
			meta: {
				allowedClaims: ['All', SystemClaim.Agent],
			},
		},
	],
};

// Account groups the merchant-facing billing dashboard (Manage) together with
// the billing report that used to live under SU Config (Billing, formerly
// labeled Overview there).
const accountPages: RoutePath = {
	path: '/account',
	component: MainLayout,
	redirect: '/account/manage',
	children: [
		{
			path: 'manage',
			name: 'Manage Organisation',
			component: BillingManage,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
			},
		},
		{
			path: 'overview',
			name: 'Billing Overview',
			component: BillingOverview,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
				hideForBillingType: [BILLING_TYPE.MANUAL],
				reuse: true,
			},
		},
		{
			path: 'subscribe',
			name: 'Billing Subscribe',
			component: BillingSubscribe,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
				hideForPaymentMethod: [PAYMENT_METHOD.BANK_TRANSFER],
			},
		},
	],
};

const releaseNotesPages: RoutePath = {
	path: '/release-notes',
	component: PublicPageLayout,
	children: [
		{
			path: '',
			name: 'Release Notes',
			component: ReleaseNotes,
			meta: {
				requiresNoAuth: true,
				allowAuthenticated: true,
				hideMarketingNavigation: true,
				title: 'Release Notes',
			},
		},
	],
};

const docsPages: RoutePath = {
	path: '/docs',
	component: PublicPageLayout,
	children: [
		{
			path: ':slug?',
			name: 'Docs',
			component: Docs,
			meta: {
				requiresNoAuth: true,
				allowAuthenticated: true,
				hideMarketingNavigation: true,
				title: 'Docs',
			},
		},
	],
};

const homePages: RoutePath = {
	path: '/home',
	component: PublicPageLayout,
	children: [
		{
			path: '',
			name: 'Home',
			component: HomePage,
			meta: {
				requiresNoAuth: true,
				allowAuthenticated: true,
				title: 'Home',
			},
		},
	],
};

const kitchenSinkPages: RoutePath = {
	path: '/kitchensink',
	component: MainLayout,
	children: [
		{
			path: '',
			component: KitchenSinkLayout,
			meta: {
				allowedClaims: ['All'],
				reuse: true,
				reuseKey: 'kitchensink',
			},
			children: [
				{
					path: '',
					name: 'Kitchen Sink',
					component: KitchenSinkOverview,
					meta: {
						allowedClaims: ['All'],
						reuse: true,
					},
				},
				{
					path: 'icons',
					name: 'Kitchen Sink Icons',
					component: KitchenSinkIcons,
					meta: {
						allowedClaims: ['All'],
						reuse: true,
					},
				},
				{
					path: 'buttons',
					name: 'Kitchen Sink Buttons',
					component: KitchenSinkButtons,
					meta: {
						allowedClaims: ['All'],
						reuse: true,
					},
				},
				{
					path: 'colors',
					name: 'Kitchen Sink Colors',
					component: KitchenSinkColors,
					meta: {
						allowedClaims: ['All'],
						reuse: true,
					},
				},
				{
					path: 'card',
					name: 'Kitchen Sink Card',
					component: KitchenSinkCard,
					meta: {
						allowedClaims: ['All'],
						reuse: true,
					},
				},
				{
					path: 'form',
					name: 'Kitchen Sink Form',
					component: KitchenSinkForm,
					meta: {
						allowedClaims: ['All'],
						reuse: true,
					},
				},
			],
		},
	],
};

const formPages: RoutePath = {
	path: '/forms',
	component: MainLayout,
	children: [
		{
			path: 'templates',
			name: 'Form Templates',
			component: FormsList,
			meta: {
				allowedClaims: [ModuleRole.FormsViewer],
				reuse: true,
			},
		},
		{
			path: 'templates/create',
			name: 'Create Form',
			component: CreateForm,
			meta: {
				allowedClaims: [ModuleRole.FormsAdmin],
			},
		},
		{
			path: 'templates/:id/edit',
			name: 'Edit Form',
			component: EditForm,
			meta: {
				allowedClaims: [ModuleRole.FormsAdmin],
			},
		},
		{
			// GET cfm/forms/:id is AllowRoles(forms:viewer) (thanos-api cfm.go:63),
			// so viewers may read a template — they just cannot save one. Same
			// component; the builder already renders read-only without forms:admin.
			path: 'templates/:id/view',
			name: 'View Form',
			component: EditForm,
			meta: {
				allowedClaims: [ModuleRole.FormsViewer],
				readOnly: true,
			},
		},
		{
			path: 'overview',
			name: 'Submissions',
			component: FormHistory,
			meta: {
				allowedClaims: [ModuleRole.FormsViewer],
				reuse: true,
			},
		},
		{
			// POST cfm/form-submissions is AllowRoles(forms:operator) (thanos-api
			// cfm.go:71). FormCompletion has no internal gating, so the route meta is
			// the gate for the whole create screen.
			path: ':formId/submit',
			name: 'New Form Submission',
			component: FormCompletion,
			meta: {
				allowedClaims: [ModuleRole.FormsOperator],
			},
		},
		{
			// PUT cfm/form-submissions/:id is AllowRoles(forms:operator) (thanos-api
			// cfm.go:75) and only ever touches a draft — the backend rejects an
			// already-submitted one, so this is the second half of create, not an
			// edit right over submitted work.
			path: 'submissions/:id/edit',
			name: 'Edit Form Submission',
			component: FormCompletion,
			meta: {
				allowedClaims: [ModuleRole.FormsOperator],
			},
		},
		{
			path: 'submissions/:id',
			name: 'Form Submission Details',
			component: FormSubmissionDetails,
			meta: {
				allowedClaims: [ModuleRole.FormsViewer],
			},
		},
	],
};

const proofOfAddressPages: RoutePath = {
	path: '/proof-of-address',
	component: MainLayout,
	children: [
		{
			path: '',
			name: 'Proof Of Address',
			component: ProofOfAddressList,
			meta: {
				// POST cases/poa/find is AllowRoles(poa:viewer) (thanos-api
				// case.go:150); only submitting a new POA needs poa:admin.
				allowedClaims: [ModuleRole.POAViewer],
				reuse: true,
			},
		},
		{
			path: 'new',
			name: 'Proof Of Address New',
			component: ProofOfAddressNew,
			meta: {
				// POST cases/poa is AllowRoles(poa:operator) (thanos-api case.go:149)
				// — submitting only creates data.
				allowedClaims: [ModuleRole.POAOperator],
			},
		},
		{
			path: ':id',
			name: 'Proof Of Address Detail',
			component: ProofOfAddressDetail,
			meta: {
				// GET cases/poa/find/:id and poa/download/:id are both
				// AllowRoles(poa:viewer) (thanos-api case.go:151-152).
				allowedClaims: [ModuleRole.POAViewer],
			},
		},
	],
};

const amlPages: RoutePath = {
	path: '/aml',
	component: MainLayout,
	redirect: '/aml/search',
	children: [
		{
			path: 'search',
			name: 'AML Search',
			component: AMLSearch,
			meta: {
				allowedClaims: [ModuleRole.AMLOperator, ModuleRole.AMLViewer],
			},
		},
		{
			path: 'history',
			name: 'AML History',
			component: AMLHistory,
			meta: {
				allowedClaims: [ModuleRole.AMLViewer],
				reuse: true,
			},
		},
		{
			path: 'history/split',
			name: 'AML History Split',
			components: {
				default: AMLHistory,
				splitView: AMLSearchResults,
			},
			meta: {
				allowedClaims: [ModuleRole.AMLViewer],
				reuse: true,
				splitFallbackRouteName: 'AML Results',
				splitMainRouteName: 'AML History',
			},
		},
		{
			path: 'searchresults',
			name: 'AML Results',
			component: AMLSearchResults,
			meta: {
				allowedClaims: [ModuleRole.AMLViewer],
				reuse: true,
			},
		},
		{
			path: 'searchdetails',
			name: 'AML Details',
			component: AMLSearchDetails,
			meta: {
				allowedClaims: [ModuleRole.AMLViewer],
			},
		},
		{
			path: 'batch/search',
			name: 'AML Batch Search',
			component: AMLBatchSearch,
			meta: {
				allowedClaims: [ModuleRole.AMLOperator, ModuleRole.AMLViewer],
			},
		},
		{
			path: 'batch/history',
			name: 'AML Batch History',
			component: AMLBatchHistory,
			meta: {
				allowedClaims: [ModuleRole.AMLViewer],
				reuse: true,
			},
		},
		{
			path: 'batch/details',
			name: 'AML Batch Details',
			component: AMLBatchDetails,
			meta: {
				allowedClaims: [ModuleRole.AMLViewer],
			},
		},
		{
			path: 'batch/download/details',
			name: 'AML Batch Download Details',
			component: AMLBatchDownloadDetails,
			meta: {
				allowedClaims: [ModuleRole.AMLViewer],
			},
		},
	],
};

const userManagementPages: RoutePath = {
	path: '/usermanagement',
	component: MainLayout,
	redirect: '/usermanagement/roles',
	children: [
		{
			path: 'roles/:roleId?',
			name: 'User Management Roles',
			component: UserManagementRoles,
			meta: {
				allowedClaims: [ModuleRole.PlatformUser],
				reuse: true,
			},
		},
		{
			path: 'newrole',
			name: 'User Management New Role',
			component: UserManagementNewRole,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
			},
		},
		{
			path: 'editrole/:roleId',
			name: 'User Management Edit Role',
			component: UserManagementEditRole,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
			},
		},
		{
			path: '/users/members',
			name: 'User Management Users',
			component: UserManagementUsers,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner, ModuleRole.PlatformUser],
				reuse: true,
			},
		},
		{
			path: 'users/split',
			name: 'User Management Users Split',
			components: {
				default: UserManagementUsers,
				splitView: UserDetailsCardNew,
			},
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner, ModuleRole.PlatformUser],
				reuse: true,
				splitFallbackRouteName: 'User Management User Profile',
				splitMainRouteName: 'User Management Users',
			},
		},
		{
			path: 'userprofile',
			name: 'User Management User Profile',
			component: UserManagementUserProfile,
			meta: {
				// This page exists to view *another* user; every read it makes is
				// platform:user (user.go:28, merchant_role.go:30,
				// user_preference.go:28, login_attempt.go:31). My Profile below is
				// the one that legitimately stays 'All'.
				allowedClaims: [ModuleRole.PlatformUser],
			},
		},
		{
			path: 'myprofile',
			name: 'User Management My Profile',
			component: UserDetailsCardNew,
			meta: {
				allowedClaims: ['All'],
			},
		},
		{
			path: 'newuser',
			name: 'User Management New User',
			component: UserManagementNewUser,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
			},
		},
	],
};

const suConfigPages: RoutePath = {
	path: '/suconfig',
	component: MainLayout,
	redirect: '/suconfig/merchants',
	children: [
		{
			path: 'merchants',
			name: 'Merchants List',
			component: MerchantList,
			meta: {
				allowedClaims: [SystemClaim.Root],
				reuse: true,
			},
		},
		{
			path: 'merchants/split',
			name: 'Merchants List Split',
			components: {
				default: MerchantList,
				splitView: MerchantProfile,
			},
			meta: {
				allowedClaims: [SystemClaim.Root],
				reuse: true,
				splitFallbackRouteName: 'Merchants Profile',
				splitMainRouteName: 'Merchants List',
			},
		},
		{
			path: 'new',
			name: 'Merchants New',
			component: MerchantNew,
			meta: {
				allowedClaims: [SystemClaim.Root],
			},
		},
		{
			path: 'profile',
			name: 'Merchants Profile',
			component: MerchantProfile,
			meta: {
				allowedClaims: [SystemClaim.Root],
			},
		},
		{
			path: 'agents',
			name: 'Merchants Agents',
			component: MerchantAgents,
			meta: {
				allowedClaims: [SystemClaim.Root],
				reuse: true,
			},
		},
		{
			path: 'trigger',
			name: 'Settings Trigger',
			component: Trigger,
			meta: {
				allowedClaims: [SystemClaim.Root],
				reuse: true,
			},
		},
		{
			path: 'trigger/split',
			name: 'Merchants Trigger Split',
			components: {
				default: Trigger,
				splitView: TriggerDetails,
			},
			meta: {
				allowedClaims: [SystemClaim.Root],
				reuse: true,
				splitFallbackRouteName: 'Settings TriggerDetails',
				splitMainRouteName: 'Settings Trigger',
			},
		},
		{
			path: 'billing',
			name: 'Billing',
			component: Billing,
			meta: {
				// The price catalog / package templates tab is guarded with
				// AllowSystemClaims(ServiceManagement) (billing.go:34/35/46-48).
				// The billing-period tab stays Root-only and gates itself.
				allowedClaims: [SystemClaim.ServiceManagement],
				reuse: true,
			},
		},
		{
			path: 'billing/split/:periodId/:merchantId',
			name: 'Billing Split',
			components: {
				default: Billing,
				splitView: BillingDetail,
			},
			meta: {
				allowedClaims: [SystemClaim.Root],
				reuse: true,
				splitFallbackRouteName: 'Billing Detail',
				splitMainRouteName: 'Billing',
			},
		},
		{
			path: 'billing/detail/:periodId/:merchantId',
			name: 'Billing Detail',
			component: BillingDetail,
			meta: {
				allowedClaims: [SystemClaim.Root],
			},
		},
		{
			path: 'archive',
			name: 'Archive',
			component: Archive,
			meta: {
				allowedClaims: [SystemClaim.Root],
			},
		},
	],
};

const flowsPages: RoutePath = {
	path: '/flows',
	component: MainLayout,
	redirect: '/flows/overview',
	children: [
		{
			// Empty path: this wrapper adds no URL segment of its own, so its
			// children resolve to /flows/overview and /flows/steps directly. It
			// exists purely so Overview and Steps share one persistent
			// Breadcrumb + SectionTabs shell instead of each being a full page
			// swap — otherwise every tab click fully remounts the page.
			path: '',
			component: FlowsOverview,
			meta: {
				allowedClaims: [ModuleRole.CFMViewer],
				reuseKey: 'flows/overview',
				reuse: true,
			},
			children: [
				{
					path: 'overview',
					name: 'risk levels',
					components: { flowsOverviewView: RiskLevels },
					meta: {
						allowedClaims: [ModuleRole.CFMViewer],
					},
				},
				{
					path: 'overview/detail',
					name: 'risk level',
					components: { flowsOverviewView: RiskLevel },
					meta: {
						allowedClaims: [ModuleRole.CFMViewer],
					},
				},
				{
					path: 'steps',
					name: 'supported checks',
					components: { flowsOverviewView: SupportedChecks },
					meta: {
						allowedClaims: [ModuleRole.CFMViewer],
					},
				},
				{
					path: 'steps/detail',
					name: 'supported check',
					components: { flowsOverviewView: SupportedCheck },
					meta: {
						allowedClaims: [ModuleRole.CFMViewer],
					},
				},
			],
		},
		{
			// Same shared-shell reasoning as above, for General | Theme.
			path: 'settings',
			component: FlowsSettings,
			redirect: '/flows/settings/general',
			meta: {
				allowedClaims: [ModuleRole.CFMAdmin],
				reuseKey: 'flows/settings',
				reuse: true,
			},
			children: [
				{
					path: 'general',
					name: 'general',
					components: { flowsSettingsView: FlowsGeneralSettings },
					meta: {
						// CFM configuration, not operator work: the save is cfm:admin
						// (cfm.go:43) and the page seeds a theme on load, which is too.
						// The global document set stacked below it is cfm:admin too
						// (cfm.go:33-37).
						allowedClaims: [ModuleRole.CFMAdmin],
					},
				},
				{
					path: 'theme',
					components: { flowsSettingsView: CfmThemeCustomizer },
					meta: {
						// The theme is merchant-wide branding: both halves of it are
						// cfm:admin (cfm.go:49/50), so an operator gets no read-only
						// view.
						allowedClaims: [ModuleRole.CFMAdmin],
					},
				},
			],
		},
	],
};

// System | Webhooks tabs on their existing list pages rather than as a
// route-level tab wrapper (neither list supports nesting under one without
// losing its split-view detail pane). The Webhooks tab is the webhook
// delivery/event log (formerly Event Logs), not the webhook subscription
// manager — that has its own page under Settings (see settingsPages).
const settingsLogsPages: RoutePath = {
	path: '/settings/logs',
	component: MainLayout,
	redirect: '/settings/logs/system',
	children: [
		{
			path: 'system',
			name: 'API Logs',
			component: Logs,
			meta: {
				// POST /devs/find and GET /devs/filters are owner reads (devs.go):
				// the request log is the merchant's whole API traffic, and the CSV
				// download is built from it.
				allowedClaims: [ModuleRole.PlatformOwner],
				reuse: true,
			},
		},
		{
			path: 'system/split',
			name: 'API Logs Split',
			components: {
				default: Logs,
				splitView: LogDetails,
			},
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
				reuse: true,
				splitFallbackRouteName: 'API LogDetails',
				splitMainRouteName: 'API Logs',
			},
		},
		{
			path: 'systemdetails',
			name: 'API LogDetails',
			component: LogDetails,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
			},
		},
		{
			path: 'eventlogs',
			name: 'API Event Logs',
			component: EventLogs,
			meta: {
				// All webhook log reads are platform:owner (webhook.go).
				allowedClaims: [ModuleRole.PlatformOwner],
				reuse: true,
			},
		},
		{
			path: 'eventlogs/split',
			name: 'API Event Logs Split',
			components: {
				default: EventLogs,
				splitView: EventLogDetails,
			},
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
				reuse: true,
				splitFallbackRouteName: 'API Event Log Details',
				splitMainRouteName: 'API Event Logs',
			},
		},
		{
			path: 'eventlogdetails',
			name: 'API Event Log Details',
			component: EventLogDetails,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
			},
		},
	],
};

const loginPage: RoutePath = {
	path: '/login',
	name: 'Login',
	component: Login,
	meta: {
		requiresNoAuth: true,
	},
};

const forgotPassPage: RoutePath = {
	path: '/forgotpass',
	name: 'ForgotPass',
	component: ForgotPass,
	meta: {
		requiresNoAuth: true,
	},
};

const unblockUserPage: RoutePath = {
	path: '/unblock',
	name: 'Unblock',
	component: Unblock,
	props: (route) => ({
		email: route.query.email,
		activationCode: route.query.activationCode,
	}),
	meta: {
		requiresNoAuth: true,
	},
};

const resetPassPage: RoutePath = {
	path: '/reset_password',
	name: 'ResetPass',
	component: ResetPass,
	meta: {
		requiresNoAuth: true,
	},
};

// Invitation links land here. Same component, same one-time token, same endpoint — the
// page reads the route name to say "set your password" to someone who has never had one
// instead of offering to reset it.
const setPassPage: RoutePath = {
	path: '/set_password',
	name: 'SetPassword',
	component: ResetPass,
	meta: {
		requiresNoAuth: true,
	},
};

// The link in an email-change verification email lands here. allowAuthenticated: the
// reader is often still signed in to the account they are changing, and a plain
// requiresNoAuth route would bounce them back to wherever they came from.
const confirmEmailChangePage: RoutePath = {
	path: '/confirm_email',
	name: 'ConfirmEmailChange',
	component: ConfirmEmailChange,
	meta: {
		requiresNoAuth: true,
		allowAuthenticated: true,
	},
};

const twoFactorPage: RoutePath = {
	path: '/two_factor',
	name: 'TwoFactor',
	component: TwoFactor,
	meta: {
		requiresNoAuth: true,
	},
};

const twoFASetupRequiredPage: RoutePath = {
	path: '/setup-mfa',
	name: 'TwoFASetupRequired',
	component: TwoFASetupRequired,
};

const notificationsPages: RoutePath = {
	path: '/settings/notifications',
	component: MainLayout,
	redirect: '/settings/notifications/configuration',
	children: [
		{
			path: 'configuration',
			name: 'Notifications Configuration',
			component: NotificationsConfiguration,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
			},
		},
		{
			path: 'customization',
			name: 'Notifications Customization',
			component: NotificationsCustomization,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
			},
		},
		{
			path: 'customization/template',
			name: 'Notifications TemplateDetails',
			component: NotificationsTemplateDetails,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
			},
		},
	],
};

// The personal inbox: one page, list + message. A message opens inline
// (:id set) instead of a separate details route — see HistoryTable.vue's
// panel/popup split.
const inboxPages: RoutePath = {
	path: '/inbox',
	component: MainLayout,
	children: [
		{
			path: '',
			name: 'Inbox',
			component: NotificationsList,
			meta: {
				// Every notification read (find, :id, count_unread) requires
				// platform:user (notification.go:31/32/37).
				allowedClaims: [ModuleRole.PlatformUser],
				// Selecting/closing a message replaces the URL (see HistoryTable.vue's
				// syncRouteToSelection) purely to keep it deep-linkable — the page
				// itself shouldn't remount and refetch every time that happens.
				reuse: true,
				reuseKey: 'Inbox',
			},
		},
		{
			path: ':id',
			name: 'Inbox Message',
			component: NotificationsList,
			meta: {
				allowedClaims: [ModuleRole.PlatformUser],
				reuse: true,
				reuseKey: 'Inbox',
			},
		},
	],
};

const casePages: RoutePath = {
	path: '/case',
	component: MainLayout,
	redirect: '/case/create',
	children: [
		{
			path: 'create',
			name: 'Case Create',
			component: CaseCreate,
			meta: {
				allowedClaims: [ModuleRole.CaseOperator],
			},
		},
		{
			path: 'history',
			name: 'Case History',
			component: CaseHistory,
			meta: {
				allowedClaims: [ModuleRole.CaseViewer],
				reuse: true,
			},
		},
		{
			path: 'history/split',
			name: 'Case History Split',
			components: {
				default: CaseHistory,
				splitView: CaseDetailsWrapper,
			},
			meta: {
				allowedClaims: [ModuleRole.CaseViewer, ModuleRole.CaseOperator],
				reuse: true,
				splitFallbackRouteName: 'Case Details',
				splitMainRouteName: 'Case History',
			},
		},
		{
			path: 'details',
			name: 'Case Details',
			component: CaseDetailsWrapper,
			meta: {
				allowedClaims: [ModuleRole.CaseViewer, ModuleRole.CaseOperator],
			},
		},
		{
			path: 'exports',
			name: 'Case Exports',
			component: CaseExports,
			meta: {
				// POST exports / GET exports/:id are case:viewer (case.go:87/88).
				allowedClaims: [ModuleRole.CaseViewer],
				reuse: true,
			},
		},
		{
			path: 'exports/details',
			name: 'Case Export Details',
			component: CaseExportDetails,
			meta: {
				allowedClaims: [ModuleRole.CaseViewer],
			},
		},
		{
			path: 'templates/:merchantSlug?',
			name: 'Settings CaseTemplates',
			component: CaseTemplates,
			meta: {
				allowedClaims: [ModuleRole.CaseOperator],
				reuse: true,
			},
		},
	],
};

const settingsPages: RoutePath = {
	path: '/settings',
	component: MainLayout,
	redirect: '/settings/templates',
	children: [
		{
			path: 'templates',
			name: 'Settings Templates',
			component: TemplateList,
			meta: {
				allowedClaims: [ModuleRole.PlatformUser],
				reuse: true,
			},
		},
		{
			path: 'templates/split',
			name: 'Settings Templates Split',
			components: {
				default: TemplateList,
				splitView: TemplateDetails,
			},
			meta: {
				allowedClaims: [ModuleRole.PlatformUser],
				reuse: true,
				splitFallbackRouteName: 'Settings TemplateDetails',
				splitMainRouteName: 'Settings Templates',
			},
		},
		{
			path: 'templatedetails',
			name: 'Settings TemplateDetails',
			component: TemplateDetails,
			meta: {
				allowedClaims: [ModuleRole.PlatformUser],
			},
		},
		{
			path: 'createtrigger',
			name: 'Settings CreateTrigger',
			component: CreateTrigger,
			meta: {
				allowedClaims: [SystemClaim.Root],
			},
		},
		{
			path: 'triggerdetails',
			name: 'Settings TriggerDetails',
			component: TriggerDetails,
			meta: {
				allowedClaims: [SystemClaim.Root],
			},
		},
		{
			path: 'createapikey',
			name: 'API CreateAPIKey',
			component: CreateAPIKey,
			meta: {
				// POST /m2m/create is platform:owner (m2m.go:34).
				allowedClaims: [ModuleRole.PlatformOwner],
			},
		},
		{
			path: 'apikeylist',
			name: 'API APIKeyList',
			component: APIKeyList,
			meta: {
				// POST /m2m/find is platform:user (m2m.go:35); create / update /
				// delete / revoke stay owner-gated inside APIKeyList and Details.
				allowedClaims: [ModuleRole.PlatformUser],
				reuse: true,
			},
		},
		{
			path: 'apikeylist/split',
			name: 'API APIKeyList Split',
			components: {
				default: APIKeyList,
				splitView: APIKeyDetails,
			},
			meta: {
				allowedClaims: [ModuleRole.PlatformUser],
				reuse: true,
				splitFallbackRouteName: 'API APIKeyDetails',
				splitMainRouteName: 'API APIKeyList',
			},
		},
		{
			path: 'apikeydetails/:id',
			name: 'API APIKeyDetails',
			component: APIKeyDetails,
			meta: {
				allowedClaims: [ModuleRole.PlatformUser],
			},
		},
		{
			path: 'createwebhook',
			name: 'API CreateWebhook',
			component: CreateWebhook,
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
			},
		},
		{
			path: 'webhooks',
			name: 'API Webhooks',
			component: Webhooks,
			meta: {
				// GET /webhooks/find is platform:owner (webhook.go), like create /
				// update / delete, which WebhooksTable also gates on owner.
				allowedClaims: [ModuleRole.PlatformOwner],
			},
		},
		{
			path: 'webhooks/split',
			name: 'API Webhooks Split',
			components: {
				default: Webhooks,
				splitView: CreateWebhook,
			},
			meta: {
				allowedClaims: [ModuleRole.PlatformOwner],
				splitFallbackRouteName: 'API CreateWebhook',
				splitMainRouteName: 'API Webhooks',
			},
		},
	],
};

interface CreateRoutesOptions {
	includeHome?: boolean;
}

export const createRoutes = ({
	includeHome = isDev(),
}: CreateRoutesOptions = {}) => [
	dashboardPages,
	accountPages,
	...(includeHome ? [homePages] : []),
	releaseNotesPages,
	docsPages,
	kitchenSinkPages,
	amlPages,
	userManagementPages,
	suConfigPages,
	flowsPages,
	settingsLogsPages,
	loginPage,
	forgotPassPage,
	unblockUserPage,
	resetPassPage,
	setPassPage,
	confirmEmailChangePage,
	twoFactorPage,
	twoFASetupRequiredPage,
	noAccessPage,
	notificationsPages,
	inboxPages,
	casePages,
	settingsPages,
	formPages,
	proofOfAddressPages,
	{
		// Without allowedClaims, Auth.isAllowed() is called with an empty list and
		// returns false for everyone but Root, so a mistyped URL bounced non-Root
		// users to the dashboard instead of showing them the 404 page.
		path: '/:pathMatch(.*)*',
		component: NotFound,
		meta: { allowedClaims: ['All'] },
	},
];

const routes = createRoutes();

export default routes;
